import React, { useEffect, useState, useRef } from 'react';
import { Search } from 'lucide-react';
import { groupChangeApi, customersApi, GroupSelectorType } from '../services/api';
import { useToast } from '../components/Toast/Toast';

export default function GroupTransfer() {
  const { showSuccess, showError } = useToast();
  const [oldGroups, setOldGroups] = useState<any[]>([]);
  const [ledgerGroups, setLedgerGroups] = useState<any[]>([]);

  const [oldGroupId, setOldGroupId] = useState<string>('');
  // Which customer-table column the selected old group maps to:
  //   'cloud' → cloud_group_id, 'group' → legacy `group`,
  //   'ledger' → ledgergroup (exact match — sub-groups excluded)
  const [oldGroupType, setOldGroupType] = useState<GroupSelectorType>('cloud');
  const [newLedgerGroupId, setNewLedgerGroupId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);
  // Customer list states
  const [customers, setCustomers] = useState<any[]>([]);
  const [custPage, setCustPage] = useState(1);
  const [custLimit, setCustLimit] = useState(25);
  const [custTotal, setCustTotal] = useState<number | null>(null);
  const [custSearch, setCustSearch] = useState('');
  const [custLoading, setCustLoading] = useState(false);

  const oldGroupRef = useRef<HTMLDivElement>(null);
  const [oldGroupSearch, setOldGroupSearch] = useState('');
  const [showOldGroupDropdown, setShowOldGroupDropdown] = useState(false);

  useEffect(() => {
    // Fetch old groups (cloud + legacy, sourced from customer table) and ledger groups
    groupChangeApi.getCustomerGroups()
      .then(res => setOldGroups(res.data || []))
      .catch(() => showError('Load failed', 'Could not load old groups'));
    groupChangeApi.getLedgerGroups()
      .then(res => setLedgerGroups(res.data || []))
      .catch(() => showError('Load failed', 'Could not load ledger groups'));
  }, [showError]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (oldGroupRef.current && !oldGroupRef.current.contains(target)) setShowOldGroupDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Label for a combined old-group option. The three kinds select on different
  // columns, so the prefix has to make clear which one you're picking:
  //   Cloud  → the assigned handler        Group → legacy admin group
  //   Ledger → the accounting group itself, DIRECT members only
  const groupKind = (g: any) => (g.type === 'ledger' ? 'Ledger' : g.type === 'group' ? 'Group' : 'Cloud');
  const groupLabel = (g: any) => `${groupKind(g)}: ${g.name}${g.cnt != null ? ` (${g.cnt})` : ''}`;

  const selectedOldGroup = oldGroups.find((g: any) => String(g.id) === String(oldGroupId) && g.type === oldGroupType);

  const handleSubmit = async () => {
    if (!oldGroupId) return showError('Validation', 'Select an old group');
    if (!newLedgerGroupId) return showError('Validation', 'Select destination ledger group');
    if (oldGroupType === 'ledger' && String(oldGroupId) === String(newLedgerGroupId)) {
      return showError('Validation', 'Source and destination ledger group are the same');
    }
    // Name both ends and the count — the old wording named neither, which made
    // an irreversible bulk write easy to confirm by accident.
    const target = ledgerGroups.find((g: any) => String(g.id) === String(newLedgerGroupId));
    const count = previewTotal != null && previewRows.length ? `${previewTotal} ledger(s)` : 'the matching ledgers';
    const scopeNote = oldGroupType === 'ledger' ? ' Ledgers in its sub-groups are NOT affected.' : '';
    if (!window.confirm(
      `Move ${count} from "${selectedOldGroup ? groupLabel(selectedOldGroup) : oldGroupId}" `
      + `to ledger group "${target?.name ?? newLedgerGroupId}"?${scopeNote}\n\nThis cannot be undone from the UI.`
    )) return;
    setSubmitting(true);
    try {
      const res = await groupChangeApi.transferLedgerGroup(oldGroupId, Number(newLedgerGroupId), null, oldGroupType);
      showSuccess('Done', res.message || `Transferred ${res.transferred || 0}`);
    } catch (err: any) { showError('Error', err.message || 'Transfer failed'); }
    finally { setSubmitting(false); }
  };

  const loadCustomers = async (page = custPage, limit = custLimit, search = custSearch) => {
    setCustLoading(true);
    try {
      const res = await customersApi.getAll(page, limit, search, 'all');
      setCustomers(res.data || []);
      setCustTotal(res.total ?? null);
      setCustPage(res.page || page);
      setCustLimit(res.limit || limit);
    } catch (err: any) {
      showError('Error', err.message || 'Failed to load customers');
    } finally {
      setCustLoading(false);
    }
  };

  useEffect(() => { loadCustomers(1, custLimit, custSearch); }, []);

  const handlePreview = async () => {
    if (!oldGroupId) return showError('Validation', 'Select an old group');
    setPreviewLoading(true);
    try {
      const res = await groupChangeApi.previewLedgerGroup(oldGroupId, 500, undefined, oldGroupType);
      setPreviewRows(res.rows || []);
      setPreviewTotal(res.total ?? (res.rows || []).length);
    } catch (err: any) {
      showError('Error', err.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-4 pb-20">
      <h1 className="text-lg md:text-2xl font-bold text-gray-900 mt-2 px-1">Group Transfer (Ledger Group)</h1>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div ref={oldGroupRef} className="relative">
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Old Group</label>
            <input
              value={showOldGroupDropdown ? oldGroupSearch : (() => { const sel = oldGroups.find(u => u.type === oldGroupType && String(u.id) === String(oldGroupId)); return sel ? groupLabel(sel) : (oldGroupId ? `#${oldGroupId}` : '— Select Old Group —'); })()}
              onChange={e => { setOldGroupSearch(e.target.value); setShowOldGroupDropdown(true); setOldGroupId(''); }}
              onFocus={() => setShowOldGroupDropdown(true)}
              placeholder="Search group / cloud group"
              className="w-full py-2 px-3 border rounded-lg"
            />
            {showOldGroupDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded shadow max-h-56 overflow-y-auto z-50">
                <div onClick={() => { setOldGroupId(''); setOldGroupType('cloud'); setOldGroupSearch(''); setShowOldGroupDropdown(false); }} className="px-3 py-2 hover:bg-gray-50 cursor-pointer italic text-gray-500">— None / Remove —</div>
                {oldGroups.filter(g => !oldGroupSearch || groupLabel(g).toLowerCase().includes(oldGroupSearch.toLowerCase()) || String(g.id).includes(oldGroupSearch)).length === 0 ? (
                  <div className="px-3 py-2 text-gray-400">No groups</div>
                ) : oldGroups.filter(g => !oldGroupSearch || groupLabel(g).toLowerCase().includes(oldGroupSearch.toLowerCase()) || String(g.id).includes(oldGroupSearch)).map(g => (
                  <div key={`${g.type}-${g.id}`} onClick={() => { setOldGroupId(String(g.id)); setOldGroupType(g.type === 'group' || g.type === 'ledger' ? g.type : 'cloud'); setOldGroupSearch(''); setShowOldGroupDropdown(false); setPreviewRows([]); setPreviewTotal(null); }} className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between gap-2">
                    <span className="truncate"><span className={`text-[10px] font-bold uppercase mr-2 px-1.5 py-0.5 rounded ${g.type === 'group' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{g.type === 'group' ? 'Group' : 'Cloud'}</span>{g.name}</span>
                    <span className="text-gray-400 text-xs whitespace-nowrap">{g.cnt} cust · #{g.id}</span>
                  </div>
                ))}
              </div>
            )}
          </div>


          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Destination Ledger Group</label>
            <select value={newLedgerGroupId} onChange={e => setNewLedgerGroupId(e.target.value)} className="w-full py-2 px-3 border rounded-lg">
              <option value="">-- Select Ledger Group --</option>
              {ledgerGroups.map(g => <option key={g.id} value={g.id}>{g.name || g.label || g.id}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-3">
          <button onClick={handlePreview} disabled={previewLoading} className="mr-3 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 disabled:opacity-50">{previewLoading ? 'Previewing...' : 'Preview'}</button>
          <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50">{submitting ? 'Processing...' : 'Transfer'}</button>
        </div>
      </div>

      {previewTotal !== null && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">Preview — Affected customers: {previewTotal}</h2>
            <button onClick={() => { setPreviewTotal(null); setPreviewRows([]); }} className="text-sm text-gray-500">Clear</button>
          </div>
          <div className="mt-2 max-h-64 overflow-y-auto text-sm">
            {previewRows.length === 0 ? (
              <div className="text-gray-500 italic">No rows returned (server limit or none found).</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600">
                    <th className="pr-4">ID</th>
                    <th className="pr-4">Company</th>
                    <th className="pr-4">Current Ledger Group</th>
                    <th className="pr-4">Reseller</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map(r => (
                    <tr key={r.id} className="border-t">
                      <td className="py-1 pr-4">{r.id}</td>
                      <td className="py-1 pr-4">{r.company}</td>
                      {/* The group each row is in TODAY — i.e. what the
                          transfer is about to overwrite. */}
                      <td className="py-1 pr-4">{r.ledgergroup_name || (r.ledgergroup ? `#${r.ledgergroup}` : '—')}</td>
                      <td className="py-1 pr-4">{r.resellerid ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Customer List */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold">All Customers</h2>
          <div className="flex items-center gap-2">
            <input value={custSearch} onChange={e => setCustSearch(e.target.value)} placeholder="Search company / id" className="py-1 px-2 border rounded" />
            <button onClick={() => loadCustomers(1, custLimit, custSearch)} className="px-3 py-1 bg-gray-200 rounded">Search</button>
            <select value={custLimit} onChange={e => { setCustLimit(Number(e.target.value)); loadCustomers(1, Number(e.target.value), custSearch); }} className="py-1 px-2 border rounded">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          {custLoading ? (
            <div className="text-gray-500">Loading...</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="pr-4">ID</th>
                  <th className="pr-4">Company</th>
                  <th className="pr-4">Cloud Group</th>
                  <th className="pr-4">LedgerGroup</th>
                  <th className="pr-4">Reseller</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} className="border-t">
                    <td className="py-1 pr-4">{c.id}</td>
                    <td className="py-1 pr-4">{c.company}</td>
                    <td className="py-1 pr-4">{c.cloud_group_id ?? '—'}</td>
                    <td className="py-1 pr-4">{c.ledgergroup ?? '—'}</td>
                    <td className="py-1 pr-4">{c.resellerid ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex items-center justify-between mt-3">
            <div className="text-sm text-gray-600">{custTotal !== null ? `Total: ${custTotal}` : ''}</div>
            <div className="flex items-center gap-2">
              <button disabled={custPage <= 1} onClick={() => loadCustomers(custPage - 1, custLimit, custSearch)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
              <div className="px-2">Page {custPage}</div>
              <button disabled={custTotal !== null && custPage * custLimit >= custTotal} onClick={() => loadCustomers(custPage + 1, custLimit, custSearch)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
